import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import './App.css';

// Redirect that PRESERVES the query string. The Plan tabs carry `?company=`, so a plain
// <Navigate to="/plan/schedule"> would silently drop a bookmarked `/schedule?company=rassini`
// onto the default company — the same wrong-company confusion we just spent a session untangling.
function RedirectKeepingQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

// Slugs that were renamed. The demo company was `rassonga` until 2026-09-28, and an unknown slug falls
// through to Chentronix's floor, so an old bookmark would open the wrong company without saying so.
const RENAMED_COMPANIES = { rassonga: 'rassini' };

function RenamedCompanyRedirect() {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const renamed = RENAMED_COMPANIES[params.get('company')];
  if (!renamed) return null;
  params.set('company', renamed);
  return <Navigate to={{ pathname, search: `?${params}` }} replace />;
}

// Auth Provider
import { MMUserProvider } from './hooks/useMMUser.jsx';

// New Pages - Manufacturing Hero
import CompanyLauncher from './pages/CompanyLauncher';

// Company Pages
import CompanyLayout from './pages/company/CompanyLayout';
import CompanyShopFloor from './pages/company/CompanyShopFloor';
import CompanyERP from './pages/company/CompanyERP';
import CompanyMetrics from './pages/company/CompanyMetrics';
import CompanySettings from './pages/company/CompanySettings';

import MachinePage from './pages/MachinePage';

// Chentronix V2 — left nav shell + redesigned pages
import ChentronixLayout from './layouts/ChentronixLayout';
import CapacitySandboxPage from './pages/chentronix/CapacitySandboxPage';
import PlanPage           from './pages/chentronix/PlanPage';
import LiveFloorPage      from './pages/chentronix/LiveFloorPage';
import LineTwinPage        from './pages/chentronix/LineTwinPage';
import FloorControlPage   from './pages/chentronix/FloorControlPage';
import ControlCenterPage from './pages/chentronix/ControlCenterPage';
import ERPPage          from './pages/chentronix/ERPPage';
import SchedulePage     from './pages/chentronix/SchedulePage';
import SettingsPage     from './pages/chentronix/SettingsPage';
import CompaniesPage    from './pages/chentronix/CompaniesPage';

// Sim definitions Command Center
import SimCommandCenter from './pages/SimCommandCenter';

// Stream admin: machine types and their fields (no MM / company UI; axios to simulator API only)
import StreamLayout from './pages/stream/StreamLayout';
import StreamMachineList from './pages/stream/StreamMachineList';
import StreamErpFleetTab from './pages/stream/StreamErpFleetTab';
import StreamMachineTypesCatalogTab from './pages/stream/StreamMachineTypesCatalogTab';
import StreamMachineTypeForm from './pages/stream/StreamMachineTypeForm';
import StreamMachineTypeCoreFields from './pages/stream/StreamMachineTypeCoreFields';
import StreamGlobalCoreFields from './pages/stream/StreamGlobalCoreFields';
import StreamMachineDetail from './pages/stream/StreamMachineDetail';
import HmiStudioPage from './pages/stream/HmiStudioPage';

// ERP Pages
import MachinesPage from './pages/erp/MachinesPage';
import PartsPage from './pages/erp/PartsPage';
import WorkOrdersPage from './pages/erp/WorkOrdersPage';
import DemandSimulator from './pages/erp/DemandSimulator';

function App() {
  // Router is provided by main.jsx (BrowserRouter for local dev, MMProvider for MM platform)
  return (
    <MMUserProvider>
      <RenamedCompanyRedirect />
      <Routes>
          {/* Home — Chentronix demo (no redirect needed; layout mounts at "/") */}
          
          {/* Company Launcher */}
          <Route path="/launcher" element={<CompanyLauncher />} />
          
          {/* Company Routes */}
          <Route path="/company/:companyId" element={<CompanyLayout />}>
            {/* Default to ERP (Machines) so we validate CRUD without loading Shop Floor / Pixi / WebSocket */}
            <Route index element={<Navigate to="erp" replace />} />
            <Route path="floor" element={<CompanyShopFloor />} />
            <Route path="erp" element={<CompanyERP />}>
              <Route index element={<Navigate to="machines" replace />} />
              <Route path="machines" element={<MachinesPage />} />
              <Route path="parts" element={<PartsPage />} />
              <Route path="work-orders" element={<WorkOrdersPage />} />
              <Route path="demand" element={<DemandSimulator />} />
            </Route>
            <Route path="metrics" element={<CompanyMetrics />} />
            <Route path="settings" element={<CompanySettings />} />
          </Route>
          
          {/* Chentronix — Spork Factory (V2 with left nav + redesigned UI) */}
          <Route path="/" element={<ChentronixLayout />}>
            {/* Live Floor is the index. It absorbed the Dashboard, which was a second view of the
                same floor over a second source of truth — `/floor` redirects rather than 404s
                because it was bookmarked and is linked from elsewhere in the app. */}
            <Route index element={<LiveFloorPage />} />
            <Route path="floor" element={<RedirectKeepingQuery to="/" />} />
            <Route path="line" element={<LineTwinPage />} />

            {/* Plan — Sandbox → Flow → Schedule are one pipeline, not three tools, so they share
                one surface and one company selection. The three old paths still resolve: they were
                bookmarked and linked from the Run bridge, so they redirect rather than 404. */}
            <Route path="plan" element={<PlanPage />}>
              <Route index element={<Navigate to="sandbox" replace />} />
              <Route path="sandbox"  element={<CapacitySandboxPage />} />
              <Route path="flow"     element={<ControlCenterPage embedded />} />
              <Route path="schedule" element={<SchedulePage embedded />} />
            </Route>
            <Route path="capacity"       element={<RedirectKeepingQuery to="/plan/sandbox" />} />
            <Route path="control-center" element={<RedirectKeepingQuery to="/plan/flow" />} />
            <Route path="schedule"       element={<RedirectKeepingQuery to="/plan/schedule" />} />
            <Route path="erp"       element={<ERPPage />} />
            <Route path="settings"  element={<SettingsPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="machine/:machineName" element={<MachinePage routePrefix="" />} />

            {/* Sim & HMI console — under the left-nav shell so the Studio keeps the
                app navbar (URLs unchanged: /stream, /stream/machine-types/:id/hmi-studio…). */}
            <Route path="stream" element={<StreamLayout />}>
              <Route index element={<StreamMachineList />} />
              <Route path="erp-fleet" element={<StreamErpFleetTab />} />
              <Route path="machine-types" element={<Outlet />}>
                <Route index element={<StreamMachineTypesCatalogTab />} />
                <Route path="global-core-keys" element={<StreamGlobalCoreFields />} />
                <Route path="new" element={<StreamMachineTypeForm />} />
                <Route path=":machineTypeId/core-fields" element={<StreamMachineTypeCoreFields />} />
                <Route path=":machineTypeId/hmi-studio" element={<HmiStudioPage />} />
                <Route path=":machineTypeId/edit" element={<StreamMachineTypeForm />} />
              </Route>
              <Route path="machines/:machineName" element={<StreamMachineDetail />} />
            </Route>
          </Route>

          {/* Floor Control — the touch/tablet control surface. Mounted OUTSIDE ChentronixLayout on
              purpose: the left nav costs ~220px, which is close to a third of an iPad portrait
              viewport, and this page is handed to someone standing up rather than browsed. */}
          <Route path="/control" element={<FloorControlPage />} />

          {/* Backward-compat: old /chentronix bookmarks redirect to / */}
          <Route path="/chentronix" element={<Navigate to="/" replace />} />
          <Route path="/chentronix/*" element={<Navigate to="/" replace />} />

          {/* Sim definitions Command Center */}
          <Route path="/sim-command-center" element={<SimCommandCenter />} />
          
          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </MMUserProvider>
  );
}

export default App;
